import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { useUpcomingInvoices } from "@/api/invoices";
import { Clock, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatDistanceToNow } from "date-fns";
import { useEffect, useState } from "react";

function formatCents(cents: number): string {
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(cents / 100);
}

export function UpcomingInvoicesTable() {
  const [page, setPage] = useState(1);
  const { data, isPending, isError, refetch, isFetching } =
    useUpcomingInvoices(page);

  useEffect(() => {
    if (data && page > Math.max(data.metadata.totalPages, 1)) {
      setPage(Math.max(data.metadata.totalPages, 1));
    }
  }, [data, page]);

  if (isPending) {
    return (
      <div className="text-center py-4 text-muted-foreground">Loading...</div>
    );
  }

  if (isError) {
    return (
      <div className="border rounded-lg p-6 text-center space-y-3">
        <p>Could not load upcoming invoices.</p>
        <div className="flex justify-center gap-2">
          {page > 1 && (
            <Button variant="outline" onClick={() => setPage(page - 1)}>
              Previous page
            </Button>
          )}
          <Button variant="outline" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (data && page > Math.max(data.metadata.totalPages, 1)) {
    return (
      <div className="text-center py-4 text-muted-foreground">Loading...</div>
    );
  }

  if (!data || data.count === 0) {
    return (
      <div className="border rounded-lg p-6 text-center text-muted-foreground">
        <Clock className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
        <p>No pending invoices. All customers are up to date.</p>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          Refresh
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock className="h-5 w-5 text-blue-500" />
          <span className="font-medium">
            {data.count} customer/payment terms group
            {data.count !== 1 ? "s" : ""} with pending orders
          </span>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          <RefreshCw
            className={`h-4 w-4 mr-2 ${isFetching ? "animate-spin" : ""}`}
          />
          Refresh
        </Button>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Customer</TableHead>
              <TableHead>Payment Terms</TableHead>
              <TableHead>Interval Progress</TableHead>
              <TableHead>Pending Amount</TableHead>
              <TableHead>Est. Trigger</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.customers.map((customer) => (
              <TableRow
                key={`${customer.customerId}:${customer.paymentTermsId}`}
              >
                <TableCell>
                  <div>
                    <p className="font-medium">{customer.customerName}</p>
                    <p className="text-xs text-muted-foreground">
                      Oldest order:{" "}
                      {formatDistanceToNow(new Date(customer.oldestOrderDate), {
                        addSuffix: true,
                      })}
                    </p>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant="outline">{customer.paymentTerms.code}</Badge>
                </TableCell>
                <TableCell>
                  <div className="space-y-1 min-w-[120px]">
                    <div className="flex items-center justify-between text-xs">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {customer.daysSinceLastInvoice}/
                        {customer.paymentTerms.dayInterval}d
                      </span>
                      <span>{customer.intervalProgress}%</span>
                    </div>
                    <Progress
                      value={customer.intervalProgress}
                      className="h-2"
                    />
                  </div>
                </TableCell>
                <TableCell>
                  <div>
                    <p className="font-medium">
                      {formatCents(customer.totalPendingCents)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {customer.completedOrderCount} completed:{" "}
                      {formatCents(customer.completedTotalCents)}
                    </p>
                  </div>
                </TableCell>
                <TableCell>
                  {customer.overallProgress >= 100 ? (
                    <Badge className="bg-green-100 text-green-800">
                      Ready now
                    </Badge>
                  ) : (
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3 text-muted-foreground" />
                      <span className="text-sm">
                        {customer.estimatedTrigger}
                      </span>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 px-2">
        <div className="text-sm text-muted-foreground">
          Showing {(page - 1) * data.metadata.limit + 1}–
          {Math.min(page * data.metadata.limit, data.count)} of {data.count}{" "}
          groups · Page {data.metadata.page} of {data.metadata.totalPages}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(page - 1)}
            disabled={page <= 1 || isFetching}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(page + 1)}
            disabled={page >= data.metadata.totalPages || isFetching}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
